import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { useParams } from 'react-router-dom'
import { TableHead } from '../../Table/TableHead'
import { TableBody } from '../../Table/TableBody'
import { Table } from '../../Table'
import { ClientItemRequisite } from '../ClientItemRequisite'
import { IClientItem } from '../../../app/modules/clients/core/_models'
import { ROLE, useAuth } from '../../../app/modules/auth'
import { getAssignedClients } from '../../../app/modules/settings/api/requisiteClientApi'
import { IsGranted } from '../../../app/modules/auth/PrivateRoute'

const ASSIGNED_PER_PAGE = 10

const AssignedClients = ({
  refresh,
  onClientsChange,
  onTotalCountChange,
}: {
  refresh: number
  onClientsChange?: (ids: number[]) => void
  onTotalCountChange?: (count: number) => void
}) => {
  const { currentUser } = useAuth()
  const params = useParams()
  const [clients, setClients] = useState<IClientItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showFlagColumn = useMemo(
    () => IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser),
    [currentUser]
  )

  const fetchClients = async (currentPage: number, currentSearch: string) => {
    if (!params.id) return
    setLoading(true)
    try {
      const res = await getAssignedClients(params.id, {
        page: currentPage,
        perPage: ASSIGNED_PER_PAGE,
        ...(currentSearch ? { search: currentSearch } : {}),
      })
      const data = res.data
      if (data.success) {
        const items: IClientItem[] = data.externalUsers || []
        const total = data.totalRows || 0
        setClients(items)
        setTotalPages(Math.ceil(total / ASSIGNED_PER_PAGE))
        onClientsChange?.(items.map((c) => c.id).filter((id): id is number => id !== undefined))
        onTotalCountChange?.(total)
      }
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchClients(page, search)
  }, [page, refresh])

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearch(value)
    if (searchDebounce.current) clearTimeout(searchDebounce.current)
    searchDebounce.current = setTimeout(() => {
      setPage(1)
      fetchClients(1, value)
    }, 500)
  }

  return (
    <div className='card mt-10'>
      <div className='py-3'>
        <div className='mb-4 px-3'>
          <input
            type='text'
            className='form-control form-control-solid'
            placeholder='Поиск по клиентам'
            onChange={onSearchChange}
          />
        </div>
        {loading ? (
          <div className='text-center py-4'>Загрузка...</div>
        ) : clients.length === 0 ? (
          <div className='text-center py-4'>Отсутствуют клиенты связанные с реквизитом</div>
        ) : (
          <Table>
            <TableHead>
              <tr>
                {showFlagColumn && <th className='min-w-120px'>flag</th>}
                <th className='min-w-120px'>external user id</th>
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <th className='min-w-150px'>мерчант</th>
                )}
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser) && (
                  <th className='min-w-250px'>комментарий</th>
                )}
                <th className='min-w-150px'>последняя активность</th>
                <th className='min-w-150px'>дата создания</th>
                <th className='min-w-60px'>удалить</th>
              </tr>
            </TableHead>
            <TableBody>
              {clients.map((el) => (
                <ClientItemRequisite
                  key={el.id ?? el.token}
                  client={el}
                  chooseClient={false}
                  active={true}
                  onSuccess={() => fetchClients(page, search)}
                  showFlagColumn={showFlagColumn}
                />
              ))}
            </TableBody>
          </Table>
        )}
        {totalPages > 1 && (
          <ReactPaginate
            activeClassName='active-pagination'
            breakClassName='item-pagination break-me-pagination'
            containerClassName='pagination-pagination'
            disabledClassName='disabled-page-pagination'
            nextClassName='item-pagination ki-duotone ki-right fs-2x'
            pageClassName='item-pagination pagination-page-pagination'
            previousClassName='item-pagination ki-duotone ki-left fs-2x'
            breakLabel='...'
            nextLabel=''
            previousLabel=''
            pageCount={totalPages}
            pageRangeDisplayed={5}
            forcePage={page - 1}
            onPageChange={({ selected }) => setPage(selected + 1)}
            renderOnZeroPageCount={null}
          />
        )}
      </div>
    </div>
  )
}

export default AssignedClients
